import { Trash, Warning } from '@phosphor-icons/react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Confirmation } from 'core/components';
import { useAgGridClientTableContext, useSelectionCountContext } from 'core/states';
import { useComponentTemplateActions } from 'elements/hooks';
import { IComponentTemplate } from 'elements/types';
import { PartNumberHelper } from 'parts/helpers';
import { Color, Variant } from 'theme/enums';

export const RemoveButton = () => {
  const [isOpen, setIsOpen] = useState(false);

  const { t } = useTranslation();

  const { selectedRows, updateRows } = useAgGridClientTableContext();
  const { selectionCount } = useSelectionCountContext();

  const { removeComponentTemplateAssociations } = useComponentTemplateActions({ selectionCount });

  const rows = selectedRows as IComponentTemplate[];
  const isDisabled = useMemo(() => rows.length === 0, [rows.length]);

  const confirmationMessage = useMemo(
    () =>
      t('messageConfirmRemoveElementTemplateParts', {
        count: rows.length,
        number: PartNumberHelper.toLabel(rows[0]?.part),
      }),
    [rows, t]
  );

  const onConfirm = async () => {
    setIsOpen(false);

    const ids = rows.map((row) => row.elementComponentTemplateAssociationId);

    const { success } = await removeComponentTemplateAssociations({ ids });

    if (!success) {
      return;
    }

    updateRows({ remove: rows });
  };

  return (
    <>
      <Button
        text={t('buttonLabelRemove')}
        variant={isDisabled ? Variant.OUTLINED : Variant.SOFT}
        color={Color.DANGER}
        startDecorator={<Trash />}
        onClick={() => setIsOpen(true)}
        disabled={isDisabled}
      />

      <Confirmation
        isOpen={isOpen}
        title={t('titleRemoveElementTemplateParts', { count: rows.length })}
        icon={<Warning weight="fill" />}
        message={confirmationMessage}
        confirmBtnLabel={t('buttonLabelRemove')}
        confirmBtnColor={Color.DANGER}
        cancelBtnLabel={t('buttonLabelCancel')}
        onConfirm={onConfirm}
        onCancel={() => setIsOpen(false)}
      />
    </>
  );
};
